import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiSearch } from 'react-icons/fi';
import { useToast } from '../../context/ToastContext';
import '../../styles/QuoteProposal.css';
import '../commissionscan/CommissionScan.css';

const getToken   = () => localStorage.getItem('toolboxToken') || '';
const getNcToken = () => localStorage.getItem('amsToken')    || '';

interface NaicsResult {
  naicsCode: string;
  description: string;
  index: number;
  transactionId: string;
  updatedInAms: boolean;
}

interface NowCertsClient {
  name: string;
  databaseId: string;
  insuredType: number;
}

const NaicsClassificationSearch: React.FC = () => {
  const [description, setDescription]       = useState('');
  const [descError, setDescError]           = useState('');
  const [clientError, setClientError]       = useState('');
  const [clientSearch, setClientSearch]     = useState('');
  const [clientName, setClientName]         = useState('');
  const [insuredDbId, setInsuredDbId]       = useState('');
  const [clientList, setClientList]         = useState<NowCertsClient[]>([]);
  const [clientLoading, setClientLoading]   = useState(false);
  const [searching, setSearching]           = useState(false);
  const [results, setResults]               = useState<NaicsResult[]>([]);
  const [updatingCode, setUpdatingCode]     = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (!clientSearch.trim()) { setClientList([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setClientLoading(true);
    debounceRef.current = setTimeout(() => fetchClients(clientSearch), 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [clientSearch]);

  const fetchClients = async (query: string) => {
    try {
      const res = await axios.get(
        `${(process.env.REACT_APP_NOWCERTSAPI           || '')}/api/nowcert/customerlist?searchText=${encodeURIComponent(query)}&nowCertsToken=${getNcToken()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setClientList(res.data || []);
    } catch { setClientList([]); }
    finally { setClientLoading(false); }
  };

  const validate = () => {
    setClientError('');
    setDescError('');
    if (!clientName) { setClientError('Please select a client'); return false; }
    if (!description.trim()) { setDescError('Please enter a NAICS description'); return false; }
    if (/[|*:"<>[\]{}\\()`';@&$!^]/.test(description)) {
      setDescError('Please enter a valid NAICS description without special characters.');
      return false;
    }
    return true;
  };

  const onSearch = async () => {
    if (!validate()) return;
    setSearching(true);
    setResults([]);
    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url       || '')}/api/naics/ai/naics`,
        { SearchText: description, InsuredName: clientName },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      if (res.data.success) {
        const enriched: NaicsResult[] = res.data.codeInfo.map((item: any, idx: number) => ({
          ...item,
          index: idx,
          transactionId: res.data.transactionId,
          updatedInAms: false,
        }));
        setResults(enriched);
      } else {
        showError(res.data.errorMessage || 'Classification failed. Please try again.');
      }
    } catch {
      showError('Unknown error while getting NAICS codes.');
    } finally {
      setSearching(false);
    }
  };

  const onUpdateAms = async (row: NaicsResult) => {
    if (!insuredDbId) return;
    setUpdatingCode(row.naicsCode);
    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url       || '')}/api/naics/updateinsureddetails?ncToken=${getNcToken()}`,
        {
          naicsCode: row.naicsCode,
          insuredDatabaseId: insuredDbId,
          transactionId: String(row.transactionId),
          naicsIndex: row.index,
          updatedInAMS: true,
        },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      if (res.data?.success === false) {
        showError(res.data.errorMessage || 'Failed to update NAICS code.');
      } else {
        setResults(prev => prev.map(r => ({ ...r, updatedInAms: r.naicsCode === row.naicsCode })));
        showSuccess('NAICS code updated successfully');
      }
    } catch (err: any) {
      showError(err.response?.data?.errorMessage || err.message || 'Unknown error while updating insured details.');
    } finally {
      setUpdatingCode('');
    }
  };

  const onClientSelect = (client: NowCertsClient) => {
    setClientName(client.name);
    setInsuredDbId(client.databaseId);
    setClientSearch('');
    setClientList([]);
    setClientError('');
  };

  return (
    <>
      <motion.div
        style={{ display: 'flex', flexDirection: 'column', gap: '20px', height: '100%' }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Page header + balance card */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)' }}>
              Classification Codes (NAICS)
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
              Append NAICS codes to your customers to easily identify their industries.
            </p>
          </div>
          {/* <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
            padding: '12px 16px', borderRadius: '10px',
            background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
            minWidth: '160px', flexShrink: 0,
          }}>
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Current Balance</span>
            <span style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>
              {balanceLoading ? '—' : `$${balance}`}
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>$0.25 charged per usage</span>
          </div> */}
        </div>

        {/* Inputs row */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {/* All three controls in one aligned row */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch', flexWrap: 'wrap' }}>
            {/* Client typeahead */}
            <div style={{ flex: '1', minWidth: '200px', position: 'relative' }}>
              <div className="cs-client-search">
                <div
                  className="cs-client-search__input-wrap"
                  style={{ borderColor: clientError ? 'var(--accent-red, #ef4444)' : undefined }}
                >
                  <FiSearch size={15} className="cs-client-search__icon" />
                  <input
                    className="cs-client-search__input"
                    type="text"
                    placeholder={clientName || 'Enter client name'}
                    value={clientSearch}
                    onChange={e => {
                      setClientSearch(e.target.value);
                      if (clientError) setClientError('');
                      if (clientName) { setClientName(''); setInsuredDbId(''); }
                    }}
                  />
                  {clientLoading && <span className="cs-client-search__spinner" />}
                </div>
                {clientList.length > 0 && (
                  <ul className="cs-client-dropdown">
                    {clientList.map(c => (
                      <li
                        key={c.databaseId}
                        className="cs-client-dropdown__item"
                        onClick={() => onClientSelect(c)}
                      >
                        <span className="cs-client-dropdown__name">{c.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {clientSearch.trim() && !clientLoading && clientList.length === 0 && (
                  <ul className="cs-client-dropdown">
                    <li className="cs-client-dropdown__item" style={{ cursor: 'default', color: 'var(--text-secondary)', opacity: 0.8 }}>
                      No clients found
                    </li>
                  </ul>
                )}
              </div>
            </div>

            {/* Description input */}
            <div
              className="cs-client-search__input-wrap"
              style={{ flex: '2', minWidth: '240px', borderColor: descError ? 'var(--accent-red, #ef4444)' : undefined }}
            >
              <input
                className="cs-client-search__input"
                type="text"
                placeholder="Enter NAICS description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && onSearch()}
              />
            </div>

            {/* Search button */}
            <button
              className="cs-browse-btn cs-browse-btn--sm"
              onClick={onSearch}
              disabled={searching}
              style={{ flexShrink: 0, marginTop: 0, alignSelf: 'stretch' }}
            >
              <FiSearch size={13} />
              {searching ? 'Searching…' : 'Search'}
            </button>
          </div>

          {/* Error / hint messages below inputs */}
          {(clientError || (clientName && !clientSearch) || descError) && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: '1', minWidth: '200px' }}>
                {clientError && (
                  <span style={{ fontSize: '11px', color: 'var(--accent-red, #ef4444)' }}>{clientError}</span>
                )}
                {clientName && !clientSearch && !clientError && (
                  <span style={{ fontSize: '11px', color: 'var(--accent-purple-light)' }}>Client: {clientName}</span>
                )}
              </div>
              <div style={{ flex: '2', minWidth: '240px' }}>
                {descError && (
                  <span style={{ fontSize: '11px', color: 'var(--accent-red, #ef4444)' }}>{descError}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Results table — only shown after a search */}
        {(searching || results.length > 0) && (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div className="qp-table-wrap glass-surface">
            <table className="qp-table">
              <thead>
                <tr>
                  <th className="qp-table__th">NAICS Code</th>
                  <th className="qp-table__th">Description</th>
                  {clientName && <th className="qp-table__th" style={{ textAlign: 'center' }}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {results.length === 0 ? (
                  <tr>
                    <td
                      colSpan={clientName ? 3 : 2}
                      style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '40px 0', fontSize: '13px' }}
                    >
                      {searching ? 'Classifying industry…' : 'No available data.'}
                    </td>
                  </tr>
                ) : results.map(row => (
                  <tr key={row.naicsCode} className="qp-table__row">
                    <td className="qp-table__td">
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: '20px',
                        fontSize: '11px', fontWeight: 600,
                        background: 'rgba(139,92,246,0.12)', color: 'var(--accent-purple-light)',
                      }}>
                        {row.naicsCode}
                      </span>
                    </td>
                    <td className="qp-table__td">{row.description}</td>
                    {clientName && (
                      <td className="qp-table__td" style={{ textAlign: 'center' }}>
                        {row.updatedInAms ? (
                          <span style={{ fontSize: '12px', color: '#065f46', fontWeight: 500 }}>✓ Updated In AMS</span>
                        ) : updatingCode === row.naicsCode ? (
                          <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Updating…</span>
                        ) : (
                          <button
                            className="cs-browse-btn cs-browse-btn--sm"
                            onClick={() => onUpdateAms(row)}
                          >
                            Update in NowCerts
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </motion.div>

    </>
  );
};

export default NaicsClassificationSearch;

